import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import userLogService from '../services/userLogService';
import './Pages.css';
import './UserLogs.css';

const UserLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Pagination and filters
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [perPage] = useState(15);

  // Filters
  const [filters, setFilters] = useState({
    action: '',
    entity_area: '',
    role: '',
    search: '',
    per_page: perPage
  });

  // Statistics
  const [stats, setStats] = useState({
    total_logs: 0,
    today_logs: 0,
    unique_users: 0,
    top_actions: [],
    top_entities: []
  });

  // Use proper dependencies for useEffect
  const filterDependencies = [
    filters.action,
    filters.entity_area,
    filters.role,
    filters.search,
    filters.per_page
  ];

  useEffect(() => {
    loadLogs();
    loadStats();
  }, [currentPage, ...filterDependencies]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const response = await userLogService.getAllLogs({
        ...filters,
        page: currentPage,
        per_page: perPage
      });

      if (response?.data?.data) {
        setLogs(response.data.data);
        setTotalPages(response.data.last_page || 1);
        setTotalLogs(response.data.total || 0);
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Error loading logs:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.message || 'Failed to load user logs',
        confirmButtonText: 'OK'
      });
      setLogs([]);
      setTotalPages(1);
      setTotalLogs(0);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await userLogService.getLogStats();
      if (response?.data?.data) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load statistics:', error);
      // Don't show error for stats as it's secondary data
    }
  };

  const handleViewLog = async (log) => {
    try {
      const response = await userLogService.getLogById(log.id);
      setSelectedLog(response.data);
      setShowViewModal(true);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.message || 'Failed to load log details',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      action: '',
      entity_area: '',
      role: '',
      search: '',
      per_page: perPage
    });
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getActionBadgeClass = (action) => {
    if (!action) return 'action-default';

    switch (action.toLowerCase()) {
      case 'create': return 'action-create';
      case 'update': return 'action-update';
      case 'delete': return 'action-delete';
      case 'login': return 'action-login';
      case 'logout': return 'action-logout';
      case 'view': return 'action-view';
      case 'generate': return 'action-generate';
      case 'change order status': return 'action-update';
      case 'create order': return 'action-create';
      default: return 'action-default';
    }
  };

  const getRoleBadgeClass = (role) => {
    if (!role) return 'role-default';

    switch (role.toLowerCase()) {
      case 'admin': return 'role-admin';
      case 'super admin': return 'role-super-admin';
      case 'cashier': return 'role-cashier';
      case 'manager': return 'role-manager';
      default: return 'role-default';
    }
  };

  const getEntityAreaClass = (entity) => {
    if (!entity) return 'entity-default';

    switch (entity.toLowerCase()) {
      case 'sales': return 'entity-sales';
      case 'authentication': return 'entity-auth';
      case 'user management': return 'entity-user';
      case 'inventory': return 'entity-inventory';
      case 'category management': return 'entity-category';
      case 'subcategory management': return 'entity-category';
      case 'cashier authentication': return 'entity-auth';
      case 'employer management': return 'entity-user';
      default: return 'entity-default';
    }
  };

  const parseJsonSafely = (jsonString) => {
    try {
      if (!jsonString || jsonString === '[]' || jsonString === '{}') return null;
      const parsed = JSON.parse(jsonString);
      return Object.keys(parsed).length > 0 ? parsed : null;
    } catch (error) {
      console.error('Failed to parse JSON:', error);
      return null;
    }
  };

  // Pagination controls (same as Orders component)
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <button
          className="pagination-btn"
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
        >
          ««
        </button>
        <button
          className="pagination-btn"
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          «
        </button>

        {startPage > 1 && (
          <>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(1)}
            >
              1
            </button>
            {startPage > 2 && <span className="pagination-ellipsis">...</span>}
          </>
        )}

        {pages}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="pagination-ellipsis">...</span>}
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(totalPages)}
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          className="pagination-btn"
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          »
        </button>
        <button
          className="pagination-btn"
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
        >
          »»
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>User Activity Logs</h1>
          <p>Monitor and track user activities</p>
        </div>
        <div className="loading">Loading activity logs...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h1>User Activity Logs</h1>
            <p>
              Monitor and track user activities across the system
              {totalLogs > 0 && ` (${totalLogs} total logs, showing ${logs.length} on this page)`}
            </p>
          </div>
          <button className="btn btn-secondary" onClick={loadLogs}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {/* <div className="stats-section">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-info">
              <h3>{stats.total_logs}</h3>
              <p>Total Logs</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📅</div>
            <div className="stat-info">
              <h3>{stats.today_logs}</h3>
              <p>Today's Activities</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">👥</div>
            <div className="stat-info">
              <h3>{stats.unique_users}</h3>
              <p>Active Users</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⚡</div>
            <div className="stat-info">
              <h3>{stats.top_actions[0]?.count || 0}</h3>
              <p>Top Action: {stats.top_actions[0]?.action || 'N/A'}</p>
            </div>
          </div>
        </div>
      </div> */}

      {/* Filters Section */}
      <div className="filters-section single-row">
        <div className="filters-row">
          <div className="filter-group">
            <input
              type="text"
              placeholder="Search by username or description..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="filter-select"
            >
              <option value="">All Actions</option>
              <option value="Create">Create</option>
              <option value="Update">Update</option>
              <option value="Delete">Delete</option>
              <option value="Login">Login</option>
              <option value="Logout">Logout</option>
              <option value="View">View</option>
              <option value="Generate">Generate</option>
              <option value="Change Order Status">Change Order Status</option>
              <option value="Create Order">Create Order</option>
            </select>
          </div>

          <div className="filter-group">
            <select
              value={filters.entity_area}
              onChange={(e) => handleFilterChange('entity_area', e.target.value)}
              className="filter-select"
            >
              <option value="">All Areas</option>
              <option value="Sales">Sales</option>
              <option value="Authentication">Authentication</option>
              <option value="User Management">User Management</option>
              <option value="Inventory">Inventory</option>
              <option value="Category Management">Category Management</option>
              <option value="SubCategory Management">SubCategory Management</option>
              <option value="Cashier Authentication">Cashier Authentication</option>
              <option value="Employer Management">Employer Management</option>
            </select>
          </div>

          <div className="filter-group">
            <select
              value={filters.role}
              onChange={(e) => handleFilterChange('role', e.target.value)}
              className="filter-select"
            >
              <option value="">All Roles</option>
              <option value="Admin">Admin</option>
              <option value="Super Admin">Super Admin</option>
              <option value="Cashier">Cashier</option>
              <option value="Manager">Manager</option>
            </select>
          </div>

          <div className="filter-actions">
            <button className="btn btn-clear" onClick={clearFilters} title="Clear Filters">
              🗑️ Clear
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="logs-table-container">
          <table className="logs-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Action</th>
                <th>Area</th>
                <th>Description</th>
                <th>IP Address</th>
                <th>Timestamp</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id}>
                  <td>
                    <div className="user-info">
                      <div className="user-avatar">
                        {log.username?.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </div>
                      <div className="user-details">
                        <strong>{log.username || 'N/A'}</strong>
                        <small>{log.user?.emp_id || 'N/A'}</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`role-badge ${getRoleBadgeClass(log.role)}`}>
                      {log.role || 'N/A'}
                    </span>
                  </td>
                  <td>
                    <span className={`action-badge ${getActionBadgeClass(log.action)}`}>
                      {log.action || 'N/A'}
                    </span>
                  </td>
                  <td>
                    <span className={`entity-badge ${getEntityAreaClass(log.entity_area)}`}>
                      {log.entity_area || 'N/A'}
                    </span>
                  </td>
                  <td className="description-cell">
                    <div className="description-text">
                      {log.description || 'No description'}
                    </div>
                  </td>
                  <td>
                    <code className="ip-address">{log.ip_address || 'N/A'}</code>
                  </td>
                  <td>
                    <div className="timestamp">
                      {formatDate(log.created_at)}
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-view"
                        onClick={() => handleViewLog(log)}
                        title="View Log Details"
                      >
                        👁️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {logs.length === 0 && (
            <div className="no-data">
              <div className="no-data-icon">📝</div>
              <h3>No Activity Logs Found</h3>
              <p>
                {filters.action || filters.entity_area || filters.role || filters.search
                  ? 'No logs match your current filters.'
                  : 'No user activities have been logged yet.'
                }
              </p>
              {filters.action || filters.entity_area || filters.role || filters.search ? (
                <button className="btn btn-primary" onClick={clearFilters}>
                  Clear Filters
                </button>
              ) : null}
            </div>
          )}
        </div>

        {/* Pagination */}
        {renderPagination()}
      </div>

      {/* View Log Modal */}
      {showViewModal && selectedLog && (
        <ViewLogModal
          log={selectedLog}
          onClose={() => setShowViewModal(false)}
          formatDate={formatDate}
          parseJsonSafely={parseJsonSafely}
          getActionBadgeClass={getActionBadgeClass}
          getRoleBadgeClass={getRoleBadgeClass}
          getEntityAreaClass={getEntityAreaClass}
        />
      )}
    </div>
  );
};

// View Log Modal Component
const ViewLogModal = ({ log, onClose, formatDate, parseJsonSafely, getActionBadgeClass, getRoleBadgeClass, getEntityAreaClass }) => {
  const oldValues = parseJsonSafely(log.old_values);
  const newValues = parseJsonSafely(log.new_values);

  const renderJsonData = (data, title) => {
    if (!data) return null;

    return (
      <div className="json-section">
        <h5>{title}</h5>
        <div className="json-content">
          {Object.entries(data).map(([key, value]) => (
            <div key={key} className="json-item">
              <strong>{key}:</strong>
              <span>
                {Array.isArray(value)
                  ? `[${value.join(', ')}]`
                  : typeof value === 'object'
                    ? JSON.stringify(value)
                    : String(value)
                }
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-large">
        <div className="modal-header">
          <h3>Activity Log Details - #{log.id}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="log-detail-view">
            {/* Log Header */}
            <div className="log-header">
              <div className="log-meta">
                <div className="meta-item">
                  <label>Log ID:</label>
                  <span>#{log.id}</span>
                </div>
                <div className="meta-item">
                  <label>Timestamp:</label>
                  <span>{formatDate(log.created_at)}</span>
                </div>
                <div className="meta-item">
                  <label>Action:</label>
                  <span className={`action-badge ${getActionBadgeClass(log.action)}`}>
                    {log.action}
                  </span>
                </div>
                <div className="meta-item">
                  <label>Entity Area:</label>
                  <span className={`entity-badge ${getEntityAreaClass(log.entity_area)}`}>
                    {log.entity_area}
                  </span>
                </div>
              </div>
            </div>

            {/* User Information */}
            <div className="user-section">
              <h4>User Information</h4>
              <div className="user-card">
                <div className="user-avatar large">
                  {log.username?.split(' ').map(n => n[0]).join('').toUpperCase()}
                </div>
                <div className="user-info-details">
                  <div className="user-main">
                    <h5>{log.username}</h5>
                    <span className={`role-badge ${getRoleBadgeClass(log.role)}`}>
                      {log.role}
                    </span>
                  </div>
                  <div className="user-secondary">
                    <p><strong>Employee ID:</strong> {log.user?.emp_id || 'N/A'}</p>
                    <p><strong>Email:</strong> {log.user?.email || 'N/A'}</p>
                    <p><strong>IP Address:</strong> <code>{log.ip_address}</code></p>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="description-section">
              <h4>Activity Description</h4>
              <div className="description-content">
                {log.description}
              </div>
            </div>

            {/* Changes Data */}
            <div className="changes-section">
              <h4>Changes Made</h4>
              <div className="changes-grid">
                {renderJsonData(oldValues, 'Old Values')}
                {renderJsonData(newValues, 'New Values')}
              </div>

              {!oldValues && !newValues && (
                <div className="no-changes">
                  No detailed change data available for this activity.
                </div>
              )}
            </div>

            {/* Additional Information */}
            <div className="additional-info">
              <h4>Additional Information</h4>
              <div className="info-grid">
                <div className="info-item">
                  <label>User ID:</label>
                  <span>{log.user_id}</span>
                </div>
                <div className="info-item">
                  <label>Last Updated:</label>
                  <span>{formatDate(log.updated_at)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserLogs;